import { Fragment, useEffect, useId, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { fetchAllPages } from "../../hooks/utils";
import { trainingApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import HistoryRow from "./HistoryRow";
import {
  MONTH_MAX_PAGES,
  MONTH_PAGE_SIZE,
  dayName,
  dayNumber,
  longDay,
  monthBounds,
  monthLabel,
  monthSummary,
  monthWeeks,
  sessionsByDay,
  shiftMonth,
  stateCounts,
  weekdays,
} from "./lib/calendar";
import { stateBadge } from "./lib/history";

const WEEKDAYS = weekdays();

// A marker per state, told apart by shape as well as colour: finished is a
// filled dot, closed itself an amber square (the darker mark amber: ≥ 3:1 on
// a chosen day and the page), open a hollow ring.
const MARKERS = {
  finished: "rounded-full bg-primary",
  auto_closed: "rounded-[2px] bg-warning-mark",
  open: "rounded-full border-[1.5px] border-primary",
};
const OTHER_MARKER = "rounded-full border-[1.5px] border-muted-foreground";
const LEGEND = ["finished", "auto_closed", "open"];

function Marker({ state }) {
  return <span aria-hidden="true" data-marker={state} className={cn("size-2 shrink-0", MARKERS[state] ?? OTHER_MARKER)} />;
}

Marker.propTypes = { state: PropTypes.string.isRequired };

// Today's number, ringed in moss wherever it falls.
const TODAY = "rounded-full bg-primary px-1.5 py-0.5 text-primary-foreground";

/**
 * History's calendar (plan-progress.md "History's calendar"): one month,
 * Sunday first, its days in the settings' time zone. A day with sessions
 * is a button named for the date and its sessions ("Tuesday, October 6:
 * 1 session, finished"), drawn with a marker per state and a count when
 * there are several; choosing it lists that day's sessions under the grid,
 * as the same rows as the list. Days without sessions are plain cells, not
 * tab stops.
 *
 * The month is read from `sessions/?from=&to=` page after page (100 at a
 * time, up to MONTH_MAX_PAGES), so a busy month isn't cut short — and if
 * that bound is ever hit, it says so. Only the newest month's answer lands:
 * flipping on stops the old month's paging and aborts its request.
 *
 * The month and the chosen day are the caller's (they live in the URL);
 * this reports moves through `onMonth` and choices through `onDay`.
 */
function HistoryCalendar({ timeZone, month, day = null, today, from = "", onMonth, onDay }) {
  const [reloadTick, setReloadTick] = useState(0);
  // The answer for one month and one try: `{key, rows, truncated, error}`.
  // Anything not keyed to what's on screen now is not shown, so a new month
  // (or a Retry) is "loading" from its first render, not after an effect.
  const [result, setResult] = useState(null);
  const request = useRef(0);
  const headingRef = useRef(null);
  const ids = useId();
  const headingId = `${ids}-month`;
  const panelId = `${ids}-day`;

  const key = `${month}:${reloadTick}`;
  const current = result?.key === key ? result : null;
  const loading = current === null;
  const error = current?.error ?? null;
  const loaded = current !== null && error === null;
  const label = monthLabel(month);
  const thisMonth = today.slice(0, 7);

  useEffect(() => {
    const id = ++request.current;
    const controller = new AbortController();
    const bounds = monthBounds(month);
    const load = async () => {
      try {
        const { items, truncated } = await fetchAllPages(
          (page) => {
            // A month flipped past stops here: its later pages are never asked for.
            if (id !== request.current) throw new Error("A newer month was asked for.");
            return trainingApi.sessions.list(
              { page, page_size: MONTH_PAGE_SIZE, from: bounds.from, to: bounds.to },
              { signal: controller.signal },
            );
          },
          { maxPages: MONTH_MAX_PAGES },
        );
        if (id !== request.current) return;
        setResult({ key, rows: items, truncated, error: null });
      } catch (err) {
        if (id !== request.current) return; // a superseded month's failure says nothing
        setResult({ key, rows: [], truncated: false, error: apiErrorMessage(err, "Something went wrong.") });
      }
    };
    load();
    return () => {
      request.current += 1; // a late answer (or a left page) lands nowhere
      controller.abort();
    };
  }, [key, month]);

  const byDay = useMemo(() => sessionsByDay(current?.rows, timeZone), [current, timeZone]);
  const weeks = useMemo(() => monthWeeks(month), [month]);
  const monthDays = [...byDay.keys()].filter((d) => d.slice(0, 7) === month);
  const monthSessions = monthDays.reduce((sum, d) => sum + byDay.get(d).length, 0);
  // The legend explains only what this month shows: its states, and a
  // count only when some day has several.
  const monthStates = new Set(monthDays.flatMap((d) => byDay.get(d).map((row) => row?.session_state)));
  const legend = LEGEND.filter((state) => monthStates.has(state));
  const mostInADay = Math.max(0, ...monthDays.map((d) => byDay.get(d).length));
  const dayRows = day ? (byDay.get(day) ?? []) : [];
  const previous = shiftMonth(month, -1);
  const next = shiftMonth(month, 1);

  const retry = () => {
    setReloadTick((tick) => tick + 1);
    headingRef.current?.focus(); // the Retry button goes with the alert
  };

  const goToday = () => {
    if (month === thisMonth) return; // already here: keep the chosen day
    onMonth(thisMonth);
  };

  const choose = (event, cell) => {
    if (event.detail > 1) return; // a double click's second press would un-choose it
    onDay(cell === day ? null : cell);
  };

  const emptyCopy =
    month > thisMonth
      ? `${label} hasn't come yet. Sessions show up here on the day they started.`
      : month === thisMonth
        ? // no "the arrows go back to earlier months": for someone new, they're empty too
          "Nothing logged this month yet. A session you start on Today shows up here on the day it started."
        : "Days you trained get a marker here. The arrows move to other months, and the List has every session, newest first.";

  const renderCell = (cell) => {
    const rows = byDay.get(cell) ?? [];
    const isToday = cell === today;
    const number = (
      <span aria-hidden="true" className={cn("leading-none", isToday && TODAY)}>
        {dayNumber(cell)}
      </span>
    );
    if (!rows.length) {
      return (
        <div
          aria-current={isToday ? "date" : undefined}
          className="flex h-12 w-full flex-col items-center justify-center rounded-md text-sm text-muted-foreground tabular-nums md:h-14"
        >
          {number}{" "}
          <span className="sr-only">{loaded ? `${longDay(cell)}: no sessions` : longDay(cell)}</span>
        </div>
      );
    }
    const chosen = cell === day;
    return (
      <button
        type="button"
        data-calendar-day={cell}
        aria-label={dayName(cell, rows)}
        aria-pressed={chosen}
        aria-current={isToday ? "date" : undefined}
        aria-controls={panelId}
        onClick={(event) => choose(event, cell)}
        className={cn(
          "relative flex h-12 w-full flex-col items-center justify-center gap-1 rounded-md border bg-card text-sm font-medium text-foreground tabular-nums transition-colors hover:border-primary/60 focus-visible:ring-[3px] focus-visible:ring-ring focus-visible:outline-none md:h-14",
          // a chosen day keeps its full moss border under the pointer too
          chosen && "border-primary bg-primary/15 hover:border-primary",
        )}
      >
        {number}
        <span aria-hidden="true" className="flex h-2 items-center gap-0.5">
          {stateCounts(rows).map(({ state }) => (
            <Marker key={state} state={state} />
          ))}
        </span>
        {rows.length > 1 && (
          <>
            {" "}
            {/* foreground at 75%: ≥ 4.5:1 on a chosen day's moss fill too
                (muted read 4.14 there) */}
            <span
              aria-hidden="true"
              data-day-count
              className="absolute top-0.5 right-1 text-[0.625rem] leading-none text-foreground/75"
            >
              ×{rows.length}
            </span>
          </>
        )}
      </button>
    );
  };

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2
          id={headingId}
          ref={headingRef}
          tabIndex={-1}
          className="m-0 mr-auto font-display text-xl font-semibold outline-none"
        >
          {label}
        </h2>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-11 md:size-9"
            aria-label={`Previous month: ${monthLabel(previous)}`}
            title={`Previous month: ${monthLabel(previous)}`}
            onClick={() => onMonth(previous)}
          >
            <ChevronLeft aria-hidden="true" />
          </Button>
          <Button type="button" variant="outline" className="h-11 md:h-9" onClick={goToday}>
            Today
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-11 md:size-9"
            aria-label={`Next month: ${monthLabel(next)}`}
            title={`Next month: ${monthLabel(next)}`}
            onClick={() => onMonth(next)}
          >
            <ChevronRight aria-hidden="true" />
          </Button>
        </div>
      </div>

      {/* What the month holds, said politely when it lands (a flip keeps
          focus on the arrow, so this is how a screen reader hears it). */}
      <p role="status" className="m-0 flex min-h-5 items-center gap-2 text-sm text-foreground/80 tabular-nums">
        {loading && (
          <>
            <Loader2 aria-hidden="true" className="size-4 animate-spin text-muted-foreground" />
            {`Loading ${label}…`}
          </>
        )}
        {loaded && (monthSessions > 0 ? monthSummary(month, monthSessions, monthDays.length) : `No sessions in ${label}.`)}
      </p>

      {error && (
        <Alert variant="destructive">
          <AlertDescription className="flex flex-wrap items-center gap-3">
            <span className="flex-1">
              Couldn&apos;t load {label}&apos;s sessions: {error}
            </span>
            <Button type="button" size="sm" variant="outline" className="h-11 md:h-8" onClick={retry}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {current?.truncated && (
        <Alert role="status">
          <AlertDescription className="text-foreground">
            {`${label} has more sessions than the calendar reads at once, so it shows the newest ${
              MONTH_PAGE_SIZE * MONTH_MAX_PAGES
            }. The List has every one, newest first.`}
          </AlertDescription>
        </Alert>
      )}

      {/* No grid under an error: an empty month would say "no sessions". */}
      {!error && (
        <table
          aria-labelledby={headingId}
          aria-busy={loading || undefined}
          className={cn(
            "w-full table-fixed border-separate border-spacing-0.5 md:border-spacing-1",
            loading && "opacity-60",
          )}
        >
          <thead>
            <tr>
              {WEEKDAYS.map((weekday) => (
                <th key={weekday.long} scope="col" className="pb-1 text-xs font-medium text-muted-foreground">
                  <span aria-hidden="true">{weekday.short}</span>{" "}
                  <span className="sr-only">{weekday.long}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week) => (
              <tr key={week.find(Boolean)}>
                {week.map((cell, i) => (
                  <td key={cell ?? `blank-${i}`} className="p-0">
                    {cell ? renderCell(cell) : null}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {loaded && monthSessions > 0 && (
        <p data-calendar-legend className="m-0 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {legend.map((state) => (
            <Fragment key={state}>
              <span className="inline-flex items-center gap-1">
                <Marker state={state} />
                {stateBadge(state).label}
              </span>{" "}
            </Fragment>
          ))}
          {mostInADay > 1 && (
            <span>{mostInADay === 2 ? "×2: two sessions that day" : `×2, ×3…: that many sessions that day`}</span>
          )}
        </p>
      )}

      <div id={panelId} className="flex flex-col gap-2">
        {loaded && monthSessions === 0 && (
          <div className="flex flex-col items-start gap-1 rounded-lg border bg-card p-4 md:p-6">
            <p className="m-0 text-muted-foreground">{emptyCopy}</p>
            {/* This month only: the way to start one. Not "Today" — the
                month's own Today button is right above. */}
            {month === thisMonth && (
              <Button asChild variant="link" className="h-11 px-0 md:h-8">
                <Link to="/training">Plan today&apos;s session</Link>
              </Button>
            )}
          </div>
        )}
        {loaded && monthSessions > 0 && !day && (
          <p className="m-0 text-sm text-muted-foreground">Choose a day with a marker to list its sessions.</p>
        )}
        {loaded && monthSessions > 0 && day && dayRows.length === 0 && (
          <p className="m-0 text-sm text-muted-foreground">No sessions on {longDay(day)}.</p>
        )}
        {loaded && dayRows.length > 0 && (
          <>
            <h3 className="m-0 font-display text-lg font-semibold">{longDay(day)}</h3>
            <ol
              aria-label={`Sessions on ${longDay(day)}`}
              data-history-list
              className="m-0 flex list-none flex-col gap-2 p-0"
            >
              {dayRows.map((row) => (
                <HistoryRow key={row.id} row={row} timeZone={timeZone} from={from} />
              ))}
            </ol>
          </>
        )}
      </div>
    </section>
  );
}

HistoryCalendar.propTypes = {
  /** The settings' IANA zone: which day each session started on. */
  timeZone: PropTypes.string,
  /** The month shown, "YYYY-MM". */
  month: PropTypes.string.isRequired,
  /** The chosen day, "YYYY-MM-DD" in `month`, or null. */
  day: PropTypes.string,
  /** Today in the settings' zone, "YYYY-MM-DD": its cell is marked and *Today* goes to its month. */
  today: PropTypes.string.isRequired,
  /** History's own query, carried to a session so it can link back. */
  from: PropTypes.string,
  /** Show another month ("YYYY-MM"). */
  onMonth: PropTypes.func.isRequired,
  /** Choose a day ("YYYY-MM-DD"), or none (null). */
  onDay: PropTypes.func.isRequired,
};

export default HistoryCalendar;
